/**
 * Gigi shopping events for PostHog.
 *
 * Loaded (deferred) by snippets/posthog.liquid only when Theme settings →
 * Analytics → "Shopping events" is on. Every event goes through
 * window.GigiAnalytics.track(), which waits for PostHog to load and for the
 * visitor's cookie choice (see the snippet header).
 *
 * Nothing here edits the sections it observes — it listens from the outside:
 *   - One fetch() wrapper sees every /cart/add, /cart/change and /cart/update
 *     request. There are 9+ add-to-cart paths (PDP single + mixed box, product
 *     grids, Starter Kit, cart upsells, the vendor product form…) and they all
 *     use fetch, so this is the one place that catches them all. Line-item
 *     properties tell the sources apart (_bundleId, _gigi_kit, _upsell).
 *   - The same wrapper sees Klaviyo client-API sign-ups (never their contents).
 *   - One delegated click listener reads the Gigi PDP buy box by its data
 *     attributes: flavour pills, plan tubs, subscribe/one-time, flavour mixer,
 *     review stars — plus quiz links anywhere on the site.
 *   - theme:cart:open (cart drawer) and the cart form's checkout submit.
 *
 * It also writes the PostHog visitor and session IDs into cart attributes
 * (_ph_did / _ph_sid, hidden at checkout by the leading underscore) so the
 * Customer Events pixel can tie checkout back to the same visit.
 *
 * No personal data is sent: no emails, names or form values.
 */
(function () {
  'use strict';

  var GA = window.GigiAnalytics;
  if (!GA || !GA.enabled || typeof window.fetch !== 'function' || window.__gigiPostHogEvents) return;
  window.__gigiPostHogEvents = true;

  var nativeFetch = window.fetch;

  function track(name, props, opts) {
    try { GA.track(name, props || {}, opts); } catch (e) { /* analytics must never break the store */ }
  }

  function currency() {
    return (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || GA.currency || null;
  }

  function r2(n) { return Math.round(n * 100) / 100; }

  /* ------------------------------------------------------------------ */
  /* Request parsing                                                     */
  /* ------------------------------------------------------------------ */

  function urlOf(input) {
    if (typeof input === 'string') return input;
    if (input && typeof input.url === 'string') return input.url;
    return String(input || '');
  }

  function pathOf(url) {
    try { return new URL(url, window.location.href).pathname; } catch (e) { return ''; }
  }

  /** Normalise a cart request body (FormData, JSON or url-encoded) to a plain object. */
  function parseBody(body) {
    if (!body) return null;
    try {
      if (typeof FormData !== 'undefined' && body instanceof FormData) {
        var o = { properties: {} };
        body.forEach(function (v, k) {
          var m = /^properties\[(.+)\]$/.exec(k);
          if (m) { o.properties[m[1]] = v; } else { o[k] = v; }
        });
        return o;
      }
      if (typeof body === 'string') {
        if (body.charAt(0) === '{') return JSON.parse(body);
        var params = new URLSearchParams(body), out = { properties: {} };
        params.forEach(function (v, k) {
          var m = /^properties\[(.+)\]$/.exec(k);
          if (m) { out.properties[m[1]] = v; } else { out[k] = v; }
        });
        return out;
      }
    } catch (e) { /* unreadable body — classify from the response alone */ }
    return null;
  }

  /** Line items from a /cart/add response: one line object, or { items: [...] }. */
  function linesOf(json) {
    if (!json) return [];
    if (Array.isArray(json.items)) return json.items;
    if (json.variant_id) return [json];
    return [];
  }

  function toItem(line) {
    var plan = line.selling_plan_allocation && line.selling_plan_allocation.selling_plan;
    var cents = line.final_price != null ? line.final_price : line.price;
    return {
      product_id: line.product_id || null,
      variant_id: line.variant_id || null,
      product: line.product_title || null,
      variant: line.variant_title || null,
      quantity: line.quantity || 0,
      price: cents != null ? cents / 100 : null,
      purchase_type: plan ? 'subscribe' : 'onetime',
      selling_plan: plan ? plan.name : null,
    };
  }

  /** Where an add came from — line properties first, then the page type. */
  function sourceOf(req, lines) {
    var props = [];
    if (req && Array.isArray(req.items)) {
      req.items.forEach(function (i) { props.push(i.properties || {}); });
    } else if (req) {
      props.push(req.properties || {});
    }
    (lines || []).forEach(function (l) { props.push(l.properties || {}); });
    function has(key) {
      return props.some(function (p) { return p && p[key] != null && p[key] !== ''; });
    }
    if (has('_bundleId')) return 'mixed_box';
    if (has('_gigi_kit')) return 'starter_kit';
    if (has('_upsell')) return 'cart_upsell';
    if (has('__shopify_send_gift_card_to_recipient')) return 'gift_card';
    switch (GA.template) {
      case 'product': return 'product_page';
      case 'index': return 'homepage';
      case 'collection': return 'collection';
      case 'cart': return 'cart_page';
      default: return GA.template || 'other';
    }
  }

  /** The section a click or submit came from, e.g. "signup" from shopify-section-sections--123__signup. */
  function placement(el) {
    var node = el || document.activeElement;
    var section = node && node.closest ? node.closest('.shopify-section') : null;
    if (!section || !section.id) return GA.template || null;
    return section.id.replace(/^shopify-section-/, '').replace(/^(template|sections)--\d+__/, '');
  }

  /* ------------------------------------------------------------------ */
  /* Cart attributes for the checkout pixel                              */
  /* ------------------------------------------------------------------ */

  var CART_IDS_KEY = 'gigi_ph_cart_ids';

  function writeCartIds(force) {
    var ids = GA.ids && GA.ids();
    if (!ids) return; // no consent yet, or counted without cookies — nothing to link
    var key = ids.did + '|' + ids.sid;
    try { if (!force && window.sessionStorage.getItem(CART_IDS_KEY) === key) return; } catch (e) { /* storage blocked */ }
    // nativeFetch, so this request never passes through the wrapper below.
    nativeFetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: { _ph_did: ids.did, _ph_sid: ids.sid } }),
    }).then(function (r) {
      if (r.ok) { try { window.sessionStorage.setItem(CART_IDS_KEY, key); } catch (e) { /* storage blocked */ } }
    }).catch(function () { /* offline — the next add retries */ });
  }

  /* ------------------------------------------------------------------ */
  /* Cart events                                                         */
  /* ------------------------------------------------------------------ */

  function onAdd(req, json) {
    var raw = linesOf(json);
    var items = raw.map(toItem);
    var source = sourceOf(req, raw);
    var value = items.reduce(function (s, i) { return s + (i.price || 0) * i.quantity; }, 0);
    var flavours = [];
    items.forEach(function (i) { if (i.variant && flavours.indexOf(i.variant) === -1) flavours.push(i.variant); });
    var props = {
      source: source,
      items: items,
      item_count: items.reduce(function (n, i) { return n + i.quantity; }, 0),
      value: r2(value),
      currency: currency(),
      purchase_type: items.some(function (i) { return i.purchase_type === 'subscribe'; }) ? 'subscribe' : 'onetime',
      is_mixed_box: source === 'mixed_box',
      flavours: flavours,
    };
    track('added_to_cart', props);
    if (source === 'cart_upsell') {
      track('cart_upsell_added', { items: items, value: props.value, currency: props.currency, purchase_type: props.purchase_type });
    }
    if (source === 'starter_kit') {
      var roles = [];
      (req && Array.isArray(req.items) ? req.items : []).forEach(function (i) {
        var role = i.properties && i.properties._gigi_kit_role;
        if (role && roles.indexOf(role) === -1) roles.push(role);
      });
      track('starter_kit_completed', { items: items, roles: roles, value: props.value, currency: props.currency, purchase_type: props.purchase_type });
    }
    writeCartIds(true);
  }

  function onAddFailed(req, status, json) {
    track('add_to_cart_failed', {
      source: sourceOf(req, []),
      status: status,
      description: (json && (json.description || json.message)) || null,
    });
  }

  function cartTotals(cart) {
    return {
      item_count: cart && cart.item_count != null ? cart.item_count : null,
      value: cart && cart.total_price != null ? r2(cart.total_price / 100) : null,
      currency: currency(),
    };
  }

  function onChange(req, cart) {
    if (!req) return;
    var qty = Number(req.quantity);
    var props = cartTotals(cart);
    props.quantity = qty;
    track(qty === 0 ? 'cart_line_removed' : 'cart_line_quantity_changed', props);
  }

  function onUpdate(req, cart) {
    if (!req || !req.updates) return; // attribute/note updates aren't shopper actions
    var updates = req.updates;
    var values = Array.isArray(updates) ? updates : Object.keys(updates).map(function (k) { return updates[k]; });
    var removed = values.filter(function (v) { return Number(v) === 0; }).length;
    if (!removed) return;
    var props = cartTotals(cart);
    props.lines_removed = removed;
    props.is_bundle = removed > 1;
    track('cart_line_removed', props);
  }

  /* ------------------------------------------------------------------ */
  /* The fetch wrapper                                                   */
  /* ------------------------------------------------------------------ */

  function kindOf(url) {
    var path = pathOf(url);
    if (/\/cart\/add(\.js)?$/.test(path)) return 'add';
    if (/\/cart\/change(\.js)?$/.test(path)) return 'change';
    if (/\/cart\/update(\.js)?$/.test(path)) return 'update';
    if (url.indexOf('a.klaviyo.com/client/') !== -1) {
      if (url.indexOf('back-in-stock') !== -1) return 'back_in_stock';
      if (url.indexOf('/subscriptions') !== -1) return 'signup';
    }
    return null;
  }

  function handle(kind, req, res, where) {
    if (kind === 'signup' || kind === 'back_in_stock') {
      if (res.ok) track(kind === 'signup' ? 'newsletter_signup' : 'back_in_stock_signup', { placement: where });
      return;
    }
    var copy;
    try { copy = res.clone(); } catch (e) { return; } // body already read — skip rather than interfere
    copy.json().then(function (json) {
      var failed = !res.ok || (json && typeof json.status === 'number' && json.status >= 400);
      if (kind === 'add') {
        if (failed) { onAddFailed(req, res.status, json); } else { onAdd(req, json); }
      } else if (!failed && kind === 'change') {
        onChange(req, json);
      } else if (!failed && kind === 'update') {
        onUpdate(req, json);
      }
    }).catch(function () { /* not JSON (e.g. a redirect page) — nothing to report */ });
  }

  window.fetch = function (input, init) {
    var pending = nativeFetch.apply(this, arguments);
    try {
      var url = urlOf(input);
      var kind = kindOf(url);
      if (kind) {
        var req = kind === 'signup' || kind === 'back_in_stock' ? null : parseBody(init && init.body);
        var where = placement();
        // Registered before the caller's own .then, so the response is cloned before they read it.
        pending.then(function (res) { handle(kind, req, res, where); }).catch(function () { /* network error */ });
      }
    } catch (e) { /* never let analytics change what fetch returns */ }
    return pending;
  };

  /* ------------------------------------------------------------------ */
  /* Gigi PDP buy box + quiz links (delegated, capture phase)            */
  /* ------------------------------------------------------------------ */

  var pdpCache = typeof WeakMap === 'function' ? new WeakMap() : null;

  function pdpData(root) {
    if (!root) return null;
    if (pdpCache && pdpCache.has(root)) return pdpCache.get(root);
    var el = root.querySelector('[data-pdp-data]');
    var data = null;
    try { data = el ? JSON.parse(el.textContent) : null; } catch (e) { data = null; }
    if (pdpCache) pdpCache.set(root, data);
    return data;
  }

  function pdpProduct(root) {
    var stars = root.querySelector('[data-pdp-stars]');
    var add = root.querySelector('[data-pdp-addbtn]');
    return {
      product_id: stars ? Number(stars.getAttribute('data-id')) || null : null,
      product_handle: add ? add.getAttribute('data-handle') : null,
    };
  }

  function indexIn(root, selector, el) {
    return Array.prototype.indexOf.call(root.querySelectorAll(selector), el);
  }

  function activeVariant(root, data) {
    var i = indexIn(root, '.gigi-pdp__flav', root.querySelector('.gigi-pdp__flav.is-active'));
    return data && data.variants ? data.variants[i > -1 ? i : 0] : null;
  }

  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;

    var quiz = t.closest('a[href*="/pages/quiz"]');
    if (quiz) { track('quiz_cta_clicked', { placement: placement(quiz) }); return; }

    // The subscriptions drawer is moved to <body>, so its terms button sits outside any section.
    var drawerTerms = t.closest('.gigi-sdrawer [data-journey-terms]');
    if (drawerTerms) { track('journey_terms_click', { placement: 'drawer' }); return; }

    var root = t.closest('.shopify-section');
    if (!root || !root.querySelector('[data-pdp-data]')) return;
    var data = pdpData(root);
    var base = pdpProduct(root);
    var props;

    var nudge = t.closest('[data-pdp-nudge-switch]');
    if (nudge) { track('pdp_nudge_switch', base); return; }

    var terms = t.closest('[data-journey-terms]');
    if (terms) { track('journey_terms_click', base); return; }

    var drawerOpen = t.closest('[data-pdp-drawer-open]');
    if (drawerOpen) { track('journey_drawer_open', base); return; }

    var flav = t.closest('.gigi-pdp__flav');
    if (flav) {
      if (flav.classList.contains('is-active')) return;
      var v = data && data.variants ? data.variants[indexIn(root, '.gigi-pdp__flav', flav)] : null;
      props = Object.assign({}, base, { flavour: v ? v.title : (flav.textContent || '').trim(), variant_id: v ? v.id : null, available: v ? !!v.available : null });
      track('flavour_selected', props);
      return;
    }

    var tub = t.closest('.gigi-pdp__tub');
    if (tub) {
      if (tub.classList.contains('is-active')) return;
      var variant = activeVariant(root, data);
      var plan = variant && variant.plans ? variant.plans[indexIn(root, '.gigi-pdp__tub', tub)] : null;
      props = Object.assign({}, base, { tubs: plan ? plan.tubs : null, plan: plan ? plan.name : null, selling_plan_id: plan ? plan.id : null });
      track('plan_selected', props);
      return;
    }

    // The mixer lives INSIDE the Subscribe card, so this check has to run
    // before the purchase-type one below: with Subscribe already active that
    // check returns early and used to swallow every + and - press, so
    // flavour_mix_changed never fired. Tubs and flavour pills are not inside
    // the mixer, so their order is unaffected.
    var step = t.closest('[data-mixer-inc], [data-mixer-dec]');
    if (step) {
      if (step.disabled) return;
      var row = step.closest('[data-mixer-row]');
      track('flavour_mix_changed', Object.assign({}, base, {
        flavour: row ? row.getAttribute('data-mixer-row') : null,
        action: step.hasAttribute('data-mixer-inc') ? 'add' : 'remove',
      }));
      return;
    }

    var type = t.closest('[data-pdp-type]');
    if (type) {
      if (type.classList.contains('is-active')) return;
      track('purchase_type_selected', Object.assign({}, base, { purchase_type: type.getAttribute('data-pdp-type') }));
      return;
    }

    if (t.closest('[data-pdp-stars]')) {
      track('reviews_clicked', base);
    }
  }, true);

  /* ------------------------------------------------------------------ */
  /* Cart drawer + checkout                                              */
  /* ------------------------------------------------------------------ */

  document.addEventListener('theme:cart:open', function () {
    nativeFetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) { track('cart_viewed', cartTotals(cart)); })
      .catch(function () { track('cart_viewed', cartTotals(null)); });
  });

  // Checkout is caught on the button click AND on the form submit: apps that guard checkout
  // (the country-restriction app does) can cancel the click and submit the form from code,
  // which fires no submit event — and a plain submit has no click when triggered by Enter.
  var lastCheckout = 0;
  function checkoutClicked() {
    var now = Date.now();
    if (now - lastCheckout < 2000) return;
    lastCheckout = now;
    // Beacon, so it survives the navigation to checkout.
    track('checkout_clicked', { source: GA.template === 'cart' ? 'cart_page' : 'cart_drawer' }, { send_instantly: true, transport: 'sendBeacon' });
  }

  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest ? e.target.closest('button[name="checkout"], input[name="checkout"]') : null;
    if (btn && btn.form && btn.form.matches('[data-cart-form], form[action$="/cart"]')) checkoutClicked();
  }, true);

  document.addEventListener('submit', function (e) {
    var form = e.target;
    var submitter = e.submitter;
    if (!form || !form.matches || !form.matches('[data-cart-form], form[action$="/cart"]')) return;
    if (!submitter || submitter.name !== 'checkout') return;
    checkoutClicked();
  }, true);

  // Link a cart that already exists (e.g. from an earlier visit) once per session.
  if (/(^|;\s*)cart=/.test(document.cookie)) {
    GA.onReady(function () { writeCartIds(false); });
  }
  /* ------------------------------------------------------------------ */
  /* Subscriber journey                                                  */
  /* ------------------------------------------------------------------ */

  // Cart drawer: the one-time -> subscription switch and its undo.
  document.addEventListener('click', function (e) {
    var t = e.target && e.target.closest ? e.target.closest('[data-cart-switch], [data-cart-unswitch]') : null;
    if (!t) return;
    track(t.hasAttribute('data-cart-switch') ? 'cart_switch_click' : 'cart_switch_undo', {
      variant_id: t.getAttribute('data-variant') ? parseInt(t.getAttribute('data-variant'), 10) : null,
      selling_plan_id: t.getAttribute('data-plan') || null,
      quantity: t.getAttribute('data-qty') ? parseInt(t.getAttribute('data-qty'), 10) : null
    });
  }, true);

  // Once per page: the switch card was rendered in the drawer.
  document.addEventListener('theme:cart:loaded', function () {
    if (window.__gigiCartSwitchSeen) return;
    var card = document.querySelector('[data-cart-switch-card]');
    if (!card) return;
    window.__gigiCartSwitchSeen = true;
    var btn = card.querySelector('[data-cart-switch]');
    track('cart_switch_shown', { variant_id: btn && btn.getAttribute('data-variant') ? parseInt(btn.getAttribute('data-variant'), 10) : null });
  });

  // PDP: the journey card scrolled into view (once), with the plan kind showing.
  (function () {
    var card = document.querySelector('[data-pdp-journey]');
    if (!card || !('IntersectionObserver' in window)) return;
    var root = card.closest('.shopify-section');
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (!en.isIntersecting) return;
        io.disconnect();
        var rail = card.querySelector('[data-journey-plan]:not([hidden])');
        track('journey_viewed', Object.assign({}, root ? pdpProduct(root) : {}, { plan_kind: rail ? rail.getAttribute('data-journey-plan') : null }));
      });
    }, { threshold: 0.5 });
    io.observe(card);
  })();

})();
